import { useEffect, useRef, useState } from 'react';
import { EditorState, type Extension } from '@codemirror/state';
import { EditorView, lineNumbers } from '@codemirror/view';
import { syntaxHighlighting } from '@codemirror/language';

import { codeHighlightStyle, codeTheme, languageExtension } from '@/ui/codemirror-theme';
import { useSelectionStore } from '../../../stores/selection-store';
import { anchorLeft, SelectionToolbar } from '../../SelectionToolbar';

interface SelectionPopup {
  x: number;
  y: number;
  startLine: number;
  endLine: number;
  snippet: string;
}

/** Read-only code viewer. Only the languages we have a CodeMirror package for are
 * syntax-highlighted (js/ts/json/css/html/md) — other files (e.g. .rs) render as plain
 * monospace text rather than pulling in a legacy-mode grab-bag for partial coverage. */
export function CodeViewer({ path, content }: { path: string; content: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const [popup, setPopup] = useState<SelectionPopup | null>(null);
  const addCode = useSelectionStore((s) => s.addCode);

  useEffect(() => {
    if (!containerRef.current) return;
    setPopup(null);

    // Shows the "Add to chat" panel below a non-empty selection; hidden again the moment the
    // selection collapses (a plain click) or the view scrolls out from under the stale position.
    // Anchored on the selection's *end* so the panel never lands on top of what was selected.
    const selectionListener = EditorView.updateListener.of((update) => {
      if (!update.selectionSet) return;
      const range = update.state.selection.main;
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      const coords = update.view.coordsAtPos(range.to);
      if (range.empty || !wrapperRect || !coords) {
        setPopup(null);
        return;
      }
      setPopup({
        x: anchorLeft(coords.left - wrapperRect.left, wrapperRect.width),
        y: coords.bottom - wrapperRect.top,
        startLine: update.state.doc.lineAt(range.from).number,
        endLine: update.state.doc.lineAt(range.to).number,
        snippet: update.state.sliceDoc(range.from, range.to),
      });
    });

    const lang = languageExtension(path);
    const extensions: Extension[] = [
      lineNumbers(),
      EditorState.readOnly.of(true),
      EditorView.editable.of(false),
      EditorView.lineWrapping,
      codeTheme,
      syntaxHighlighting(codeHighlightStyle),
      selectionListener,
      ...(lang ? [lang] : []),
    ];

    const view = new EditorView({
      state: EditorState.create({ doc: content, extensions }),
      parent: containerRef.current,
    });
    viewRef.current = view;

    const hidePopup = () => setPopup(null);
    view.scrollDOM.addEventListener('scroll', hidePopup, { passive: true });

    return () => {
      view.scrollDOM.removeEventListener('scroll', hidePopup);
      view.destroy();
    };
  }, [path, content]);

  return (
    <div ref={wrapperRef} className="relative min-h-0 flex-1 overflow-auto text-xs [&_.cm-editor]:h-full">
      <div ref={containerRef} className="h-full" />
      {popup && (
        <SelectionToolbar
          style={{ left: popup.x, top: popup.y + 6 }}
          onCancel={() => setPopup(null)}
          onAdd={(annotate) => {
            addCode({ path, startLine: popup.startLine, endLine: popup.endLine, snippet: popup.snippet }, annotate);
            setPopup(null);
          }}
        />
      )}
    </div>
  );
}
